import React from 'react';
import { Box, Typography, Paper, IconButton } from '@mui/material';
import {
  ArrowUpward as UpArrow,
  ArrowDownward as DownArrow,
  ArrowDropDown as DropDownIcon
} from '@mui/icons-material';

/**
 * Individual Metric Card matching the GA snapshot style
 */
const MetricCard = ({ title, value, change, isPositive, active }) => {
  const color = isPositive ? '#1e8e3e' : '#d93025'; // Google standard colors
  const Icon = isPositive ? UpArrow : DownArrow;

  return (
    <Box sx={{ 
      p: { xs: 1.5, sm: 2.5 }, 
      minWidth: { xs: 140, sm: 180 }, 
      flex: 1,
      borderRight: '1px solid #e0e0e0',
      '&:last-child': { borderRight: 'none' },
      position: 'relative',
      bgcolor: active ? 'rgba(241, 243, 244, 1)' : 'transparent',
      borderBottom: active ? '3px solid #1a73e8' : '3px solid transparent',
      transition: 'all 0.2s',
      '&:hover': { bgcolor: 'rgba(0,0,0,0.02)' },
      cursor: 'pointer'
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <Typography 
          variant="body2" 
          sx={{ 
            color: active ? '#1a73e8' : '#5f6368', 
            fontWeight: 500, 
            fontSize: '0.875rem',
            display: 'flex',
            alignItems: 'center'
          }}
        >
          {title}
          <DropDownIcon sx={{ fontSize: 18, ml: 0.5, color: active ? '#1a73e8' : '#70757a' }} />
        </Typography>
      </Box>
      
      <Typography variant="h4" sx={{ fontWeight: 400, mb: 1, color: '#202124', letterSpacing: '-0.5px' }}>
        {new Intl.NumberFormat().format(value)}
      </Typography>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Icon sx={{ fontSize: 14, color: color, strokeWidth: 2 }} />
        <Typography variant="body2" sx={{ color: color, fontWeight: 600, fontSize: '0.875rem' }}>
          {Math.abs(change)}%
        </Typography>
      </Box>
    </Box>
  );
};

/**
 * Main Comparison Section
 */
const MetricComparison = ({ data, loading }) => {
  if (!data && !loading) return null;

  const comparison = data?.comparison || {};

  const metrics = [
    { 
      title: 'Returning users', 
      value: comparison.returningUsers?.current || 0, 
      change: comparison.returningUsers?.change || 0,
      isPositive: (comparison.returningUsers?.change || 0) >= 0
    },
    { 
      title: 'Active users', 
      value: comparison.activeUsers?.current || 0, 
      change: comparison.activeUsers?.change || 0,
      isPositive: (comparison.activeUsers?.change || 0) >= 0,
      active: true
    },
    { 
      title: 'New users', 
      value: comparison.newUsers?.current || 0, 
      change: comparison.newUsers?.change || 0,
      isPositive: (comparison.newUsers?.change || 0) >= 0
    },
    { 
      title: 'Sessions', 
      value: comparison.sessions?.current || 0, 
      change: comparison.sessions?.change || 0,
      isPositive: (comparison.sessions?.change || 0) >= 0
    }
  ];

  return (
    <Paper 
      elevation={0} 
      variant="outlined" 
      sx={{ 
        borderRadius: 2, 
        overflowX: 'auto', 
        mb: 2,
        borderColor: '#dadce0',
        display: 'flex',
        flexWrap: 'nowrap',
        '&::-webkit-scrollbar': { display: 'none' } // Hide scrollbar for clean look
      }}
    >
      {metrics.map((m, i) => (
        <MetricCard key={i} {...m} />
      ))}
    </Paper>
  );
};

export default MetricComparison;
